import React, { useMemo, useState } from "react";
import { deg2rad, fmtAbs1 } from "../utils/wcaMath";
import { Link } from "react-router-dom";

function CrosswindPage() {
  const [aircraftHeading, setAircraftHeading] = useState("250");
  const [windDirection, setWindDirection] = useState("300");
  const [windSpeed, setWindSpeed] = useState("15");
  const [windGust, setWindGust] = useState("");

  const crosswindData = useMemo(() => {
    const heading = Number(aircraftHeading);
    const windDir = Number(windDirection);
    const windSpd = Number(windSpeed);
    const gust = Number(windGust);

    if (
      !Number.isFinite(heading) ||
      !Number.isFinite(windDir) ||
      !Number.isFinite(windSpd)
    ) {
      return null;
    }

    const angleDiff = windDir - heading;
    const angleRad = deg2rad(angleDiff);

    const crosswind = windSpd * Math.sin(angleRad);
    const headwind = windSpd * Math.cos(angleRad);

    const gustCrosswind = Number.isFinite(gust)
      ? gust * Math.sin(angleRad)
      : null;

    const gustHeadwind = Number.isFinite(gust)
      ? gust * Math.cos(angleRad)
      : null;

    return {
      angleDiff,
      crosswind,
      headwind,
      gustCrosswind,
      gustHeadwind,
    };
  }, [aircraftHeading, windDirection, windSpeed, windGust]);

  return (
    <>   {/* REACT Fragment: groups multiple elements without adding extra div to DOM */}
    <div>
      <section className="tool-hero">
        <h1>Crosswind Calculator</h1>

        <p style={{ fontSize: "18px" }}>
          Calculate crosswind and headwind components using aircraft heading,
          wind direction, and wind speed. This tool helps pilots evaluate
          takeoff and landing conditions.
        </p>
      </section>
      <div className="ad-banner">Ad Space</div>

        <p style={{fontSize: "18px", color: "#555"}}>
          Enter aircraft heading, wind direction, and wind speed to calculate
          crosswind and headwind/tailwind components.
        </p>

        <section className="home-tool-section">
          <div className="pa-da-grid">
            <div className="crosswind-inputs-block">
                <h4>Inputs</h4>

                <div className="crosswind-input-grid">
                  <label>Heading (°)</label>
                  <input 
                    style={{fontSize: "18px", fontWeight: "700"}}
                    value={aircraftHeading} onChange={(e) => setAircraftHeading(e.target.value)} />

                  <label >Wind Direction (°)</label>
                  <input 
                  style={{fontSize: "18px", fontWeight: "700"}}
                  value={windDirection} onChange={(e) => setWindDirection(e.target.value)} />

                  <label>Wind Speed (kt)</label>
                  <input 
                    style={{fontSize: "18px", fontWeight: "700"}}
                    value={windSpeed} onChange={(e) => setWindSpeed(e.target.value)} />

                  <label>Wind Gust (kt)</label>
                  <input 
                    style={{fontSize: "18px", fontWeight: "700"}}
                    value={windGust} onChange={(e) => setWindGust(e.target.value)} placeholder="optional" />
                </div>
              </div>

             <div className="crosswind-results-block">
              <h4>Results</h4>

              <div className="pa-da-results">
                <div className="pa-da-result-row">
                  <span className="pa-da-result-label">Crosswind:</span>
                  <span className="pa-da-result-value">
                    {crosswindData ? fmtAbs1(crosswindData.crosswind) : "---"}
                  </span>
                  <span className="pa-da-result-unit">kt</span>
                </div>

                <div className="pa-da-result-row">
                  <span className="pa-da-result-label">Direction:</span>
                  <span className="pa-da-result-value">
                    {crosswindData
                      ? crosswindData.crosswind > 0
                        ? "from right"
                        : crosswindData.crosswind < 0
                        ? "from left"
                        : "none"
                      : "---"}
                  </span>
                </div>

                <div className="pa-da-result-row">
                  <span className="pa-da-result-label">Headwind:</span>
                  <span className="pa-da-result-value">
                    {crosswindData
                      ? crosswindData.headwind >= 0
                        ? fmtAbs1(crosswindData.headwind)
                        : "0.0"
                      : "---"}
                  </span>
                  <span className="pa-da-result-unit">kt</span>
                </div>

                <div className="pa-da-result-row">
                  <span className="pa-da-result-label">Tailwind:</span>
                  <span className="pa-da-result-value">
                    {crosswindData
                      ? crosswindData.headwind < 0
                        ? fmtAbs1(crosswindData.headwind)
                        : "0.0"
                      : "---"}
                  </span>
                  <span className="pa-da-result-unit">kt</span>
                </div>

                {crosswindData?.gustCrosswind !== null && (
                  <>
                    <div className="pa-da-result-row">
                      <span className="pa-da-result-label">Gust Crosswind:</span>
                      <span className="pa-da-result-value">
                        {fmtAbs1(crosswindData.gustCrosswind)}
                      </span>
                      <span className="pa-da-result-unit">kt</span>
                    </div>

                    <div className="pa-da-result-row">
                      <span className="pa-da-result-label">Gust HW/TW:</span>
                      <span className="pa-da-result-value">
                        {crosswindData.gustHeadwind >= 0
                          ? `${fmtAbs1(crosswindData.gustHeadwind)} HW`
                          : `${fmtAbs1(crosswindData.gustHeadwind)} TW`}
                      </span>
                      <span className="pa-da-result-unit">kt</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="related-tools-section">
          <div className="tool-content">
        <h2>Related Tools</h2>

      <p>
        <Link to="/">HomePage</Link> |
        <Link to="/vsps">Vspeed</Link> |
        <Link to="/rollout">Ground Roll</Link> |
        <Link to="/navlog">NavLog</Link> |
        <Link to="/wtbals">Weight & Balance</Link> |
        <Link to="/density-altitude">Density Altitude</Link> |
        <Link to="/cw">Crosswind/Headwind</Link> |
        <Link to="/mnvr">Maneuvers</Link> |
        <Link to="/airframe-powerplant">Airframe & Powerplant</Link> |
        <Link to="/airplane-prebuy">Airplane Prebuy Checklist</Link> |
        <Link to="/adjustable-amortization">Aircraft Loan</Link>
      </p>
      </div>      {/* closes tool content section */}
      </section>  {/* closes related tools section */}



    <div className="ad-banner">Ad Space</div>

      <div className="tool-content">
        <h2>What is Crosswind?</h2>

          <p>
          Crosswind is the component of wind that acts perpendicular to the aircraft's 
          direction of travel. Strong crosswinds can make takeoff and landing more difficult 
          and may exceed aircraft or pilot limitations.
          </p>

          <h2>How to Calculate Crosswind</h2>

          <p>
          Crosswind is calculated using the wind speed and the angle between the wind
          direction and the aircraft heading. As this angle increases toward 90°, the
          crosswind component increases.

          </p>

          <h2>Why It Matters</h2>

          <p>
          Pilots must ensure that crosswind conditions do not exceed the aircraft's maximum
          demonstrated crosswind capability or their personal limitations. Excessive 
          crosswinds can lead to loss of directional control during takeoff and landing.
          </p>
          
          </div>  {/* closes tool content section */}
          </div>
      </>
  );
}

export default CrosswindPage;